import { HttpClient } from '@angular/common/http';
import { Component, OnInit, OnChanges, ViewChild, Output, Input, EventEmitter } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { QuillEditorComponent } from 'ngx-quill';
import { SettingsConstants } from 'projects/is-ui-kit/src/lib/core/settings/settings.service';
import { IsDeleteNoteService } from '../../dialogs/is-delete-note/is-delete-note.service';
import { MatMenuTrigger } from '@angular/material/menu';
import moment from 'moment';
import { DatePipe } from '@angular/common';
import { DateAdapter } from '@angular/material/core';
import { switchMap, catchError } from 'rxjs/operators';
import { of } from 'rxjs';

@Component({
  selector: 'is-note-list',
  templateUrl: './is-note-list.component.html',
  styleUrls: ['./is-note-list.component.scss']
})
export class IsNoteListComponent implements OnInit, OnChanges {

  @Input() public notesList: any;
  @Input() public notesListTotal: number = 0;
  @Input() public dataExperienceGeneralNotes: boolean = false;
  @Input() public showSpinner: boolean = false;
  @Output() public editNoteEmitter = new EventEmitter();
  @Output() public deleteNoteEmitter = new EventEmitter();
  @Output() public newGeneralNoteEmitter = new EventEmitter();
  @Output() public changeRefferedToEmitter = new EventEmitter();
  @Output() public changeHighlightedEmitter = new EventEmitter();
  @Output() public exportExcelEmitter = new EventEmitter();
  @Output() public emitOpenFullscreen = new EventEmitter();

  @ViewChild('highlightedOptionMenuTrigger') highlightedOptionMenuTrigger: MatMenuTrigger;
  @ViewChild('generalOptionMenuTrigger') generalOptionMenuTrigger: MatMenuTrigger;
  public noteListGeneral: any = [];
  public noteListHighlighted: any = [];
  public authorList: any = [];
  public user: any = SettingsConstants.LOGGEDUSER;
  public highlightedNotes = this._translate.instant('highlighted_notes');
  public showAddNote: boolean = false;

  // quill-editor for view
  public quillConfigReadOnly = {
    toolbar: false
  };
  // quill-editor for new and edit
  @ViewChild('editEditor') editEditor: QuillEditorComponent;
  public quillConfig = {
    toolbar: {
      container: [
        ['bold', 'italic', 'underline']
      ]
    },
    mention: {
      allowedChars: /^[A-Za-z\sÅÄÖåäö]*$/,
      mentionDenotationChars: ['@'],
      defaultMenuOrientation: 'bottom',
      onSelect: (item, insertItem) => {
        const editor = this.editEditor.quillEditor;
        insertItem(item);
        editor.insertText(editor.getLength() - 1, '', 'user');
      },
      source: (searchTerm, renderList) => { // mention
        this.authorList = [];
        if (searchTerm !== '') {
          this.getAuthorsListMention(`${SettingsConstants.BACKENDURL}/inspiring/isauth/api/user/?search=${searchTerm}`, renderList, searchTerm);
        } else {
          this.getAuthorsListMention(`${SettingsConstants.BACKENDURL}/inspiring/isauth/api/user/`, renderList, searchTerm);
        }
      }
    }
  };

  constructor(
    private _http: HttpClient,
    private _deleteNoteService: IsDeleteNoteService,
    private _translate: TranslateService,
    private _datePipe: DatePipe,
    private _dateAdapter: DateAdapter<Date>
  ) {
    this._dateAdapter.setLocale('en-GB'); // dd/MM/yyyy
  }

  ngOnInit(): void {
  }

  ngOnChanges(): void {
    this.noteListHighlighted = [];
    this.noteListGeneral = [];
    if (this.notesList.length !== 0) {
      this.notesList.forEach((note) => {
        note['dateFrom'] = new UntypedFormControl(null);
        note['timeFrom'] = new UntypedFormControl('00:00');
        note['dateTo'] = new UntypedFormControl(null);
        note['timeTo'] = new UntypedFormControl('23:59');
        note['note'] = false;
        note['newText'] = '';
        if (note.valid_from !== null && note.valid_from !== undefined) {
          const time = this.extractTime(note.valid_from);
          note['dateFrom'].setValue(note.valid_from);
          note['timeFrom'].setValue(time);
          note['valid_from_formatted'] = this.getFormattedDate(note.valid_from);
        }
        if (note.valid_to !== null && note.valid_to !== undefined) {
          const time = this.extractTime(note.valid_to);
          note['dateTo'].setValue(note.valid_to);
          note['timeTo'].setValue(time);
          note['valid_to_formatted'] = this.getFormattedDate(note.valid_to);
        }
        note['creation_datetime_formatted'] = this.getFormattedDate(note.creation_datetime);
        note['update_datetime_formatted'] = this.getFormattedDate(note.update_datetime);
        if (note?.pinned === true) {
          this.noteListHighlighted.push(note);
        } else {
          this.noteListGeneral.push(note);
        }
      });
    }
  }

  public getAuthorsListMention(url, renderList, searchTerm): void {
    this.authorList = [];
    this._http.get(url).pipe(
      switchMap((data: any) => {
        this.authorList = data.results.map((author: any) => ({
          id: author.id,
          value: author?.str || author?.first_name + ' ' + author?.last_name
        }));

        return of(this.authorList);
      }),
      catchError((error: Error) => {
        console.error(error);
        return of([]); // Return an empty array if there's an error
      })
    ).subscribe((data: any) => {
      renderList(data, searchTerm);
    });
  }

  public extractTime(dateTimeStr: string): any {
    const originalDateTime = new Date(dateTimeStr);
    const hours = originalDateTime.getHours();
    const minutes = originalDateTime.getMinutes();
    // const seconds = originalDateTime.getSeconds();
    // const formattedTime = `${hours < 10 ? '0' : ''}${hours}:${minutes < 10 ? '0' : ''}${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
    const formattedTime = `${hours < 10 ? '0' : ''}${hours}:${minutes < 10 ? '0' : ''}${minutes}`;
    return formattedTime;
  }

  public getFormattedDate(originalDate): string {
    const date = new Date(originalDate);
    return this._datePipe.transform(date, 'dd/MM/yyyy HH:mm') || '';
  }

  // prevent matMenu closing
  public preventPanelClosing(event: any): void {
    event.stopPropagation();
  }

  public addNewNote(): void {
    this.showAddNote = true;
  }

  public closeShowAddNote(event): void {
    this.showAddNote = false;
  }

  public exportNoteList(event): void {
    this.exportExcelEmitter.next(this.notesList);
  }

  public openFullScreeNoteList(event): void {
    this.emitOpenFullscreen.next(event);
  }

  public createNewGeneralNote(note): void {
    this.showAddNote = false;
    this.newGeneralNoteEmitter.next(note);
  }

  // SINGLE NOTE ACTIONS
  public changeHighlighted(note): void {
    const obj = {
      id: note.id,
      pinned: !note.pinned,
      entity: note.entity,
      entity_id: note.entity_id
    };
    this.changeHighlightedEmitter.emit(obj);
  }

  public changeRefferedTo(note, newReffered): void {
    const obj = {
      id: note.id,
      entity: newReffered
    };
    this.changeRefferedToEmitter.next(obj);
  }

  public editNote(event, note): void {
    event.stopPropagation();
    if (this.highlightedOptionMenuTrigger?.menuOpen) {
      this.highlightedOptionMenuTrigger.closeMenu();
    }
    if (this.generalOptionMenuTrigger?.menuOpen) {
      this.generalOptionMenuTrigger.closeMenu();
    }
    note.isEdit = true;
    note.newText = note.text;
  }

  public deleteNote(event, note): void {
    event.stopPropagation();
    if (this.highlightedOptionMenuTrigger?.menuOpen) {
      this.highlightedOptionMenuTrigger.closeMenu();
    }
    if (this.generalOptionMenuTrigger?.menuOpen) {
      this.generalOptionMenuTrigger.closeMenu();
    }
    this._deleteNoteService.openDeleteNoteDialog(note, (result: any) => {
      if (result) {
        this.deleteNoteEmitter.emit(result);
      }
    });
  }

  public setSubmitDisabled(note): boolean {
    if (note.newText === '') {
      return true;
    } else {
      return false;
    }
  }

  public submit(event, note): void {
    let finalFromDate: string = null;
    if (note.dateFrom.value !== null) {
      let tempTimeFrom = '00:00';
      if (note.timeFrom.value !== '' && note.timeFrom.value !== null && note.timeFrom.value !== undefined) {
        tempTimeFrom = note.timeFrom.value;
      }
      const timeFromTemp = moment(tempTimeFrom, 'hmm').format('HH:mm') !== 'Invalid date' ? moment(tempTimeFrom, 'hmm').format('HH:mm') : tempTimeFrom;
      finalFromDate = this.convertDateTime(note.dateFrom.value, timeFromTemp);
    }
    let finalToDate: string = null;
    if (note.dateTo.value !== null) {
      let tempTimeTo = '23:59';
      if (note.timeTo.value !== '' && note.timeTo.value !== null && note.timeTo.value !== undefined) {
        tempTimeTo = note.timeTo.value;
      }
      const timeToTemp = moment(tempTimeTo, 'hmm').format('HH:mm') !== 'Invalid date' ? moment(tempTimeTo, 'hmm').format('HH:mm') : tempTimeTo;
      finalToDate = this.convertDateTime(note.dateTo.value, timeToTemp);
    }
    const newNote = {
      id: note.id,
      text: note.newText,
      valid_from: finalFromDate,
      valid_to: finalToDate,
      entity: note.entity,
      entity_id: note.entity_id
    };
    note.isEdit = false;

    this.editNoteEmitter.emit(newNote);
  }

  public convertDateTime(dateString: string, timeString: string): string {
    const originalDate = new Date(dateString);
    const day = originalDate.getDate();
    const month = originalDate.getMonth() + 1;
    const year = originalDate.getFullYear();
    const formattedDate = `${year}-${month < 10 ? '0' : ''}${month}-${day < 10 ? '0' : ''}${day}`;
    const desiredDateTimeStr = `${formattedDate}T${timeString}:00`;
    return desiredDateTimeStr;
  }

  public cancel(event, note): void {
    this.noteListGeneral.forEach((not) => {
      if (not.id === note.id) {
        note.isEdit = false;
        note.newText = '';
        note.dateFrom.setValue(note.valid_from);
        note.dateTo.setValue(note.valid_to);
        note.timeFrom.setValue('00:00');
        note.timeTo.setValue('23:59');
      }
    });
    this.noteListHighlighted.forEach((not) => {
      if (not.id === note.id) {
        note.isEdit = false;
        note.newText = '';
        note.dateFrom.setValue(note.valid_from);
        note.dateTo.setValue(note.valid_to);
        note.timeFrom.setValue('00:00');
        note.timeTo.setValue('23:59');
      }
    });
  }

  public checkPermission(permissions: string | string[]): boolean {
    if (typeof (permissions) === 'string') {
      if (SettingsConstants.USERPERMISSIONS[permissions]) {
        return SettingsConstants.USERPERMISSIONS[permissions];
      } else {
        return false;
      }
    } else {
      for (let i = 0; i < permissions.length; i++) {
        if (!(permissions[i] in SettingsConstants.USERPERMISSIONS)) {
          return false;
        }
      }
      return true;
    }
  }
}
